import { useEffect, useState } from "react";
import { useProject } from "../context/ProjectContext";
import {
  deleteForm,
  getFormsByProjectId,
  getSubmissionsByFormId,
} from "../services/apiService";
import { FormFA } from "../interfaces/global-interfaces";
import { Submissions } from "../components/Submissions";
import { UpsertProjectForm } from "../components/UpsertProjectForm";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  ArrowLeft,
  ChevronDown,
  FileText,
  Plus,
  Search,
  Inbox,
  Trash2,
  Pencil,
} from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { SearchInput } from "../components/ui/SearchInput";
import { EmptyState } from "../components/ui/EmptyState";
import { Loader } from "../components/ui/Loader";
import { useToast } from "../components/ui/Toast";
import { staggerDelay } from "../utils/animation";

export const Forms = () => {
  const { formId } = useParams<{ formId: string }>();
  const { selectedProject, setSelectedProject } = useProject();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [forms, setForms] = useState<FormFA[]>([]);
  const [selectedForm, setSelectedForm] = useState<FormFA | undefined>(
    undefined,
  );
  const [openFormIds, setOpenFormIds] = useState<string[]>([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingForm, setEditingForm] = useState<FormFA | null>(null);
  const [submissions, setSubmissions] = useState<any>(undefined);
  const [submissionsPage, setSubmissionsPage] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalShowDeleteBtn, setModalShowDeleteBtn] = useState(false);

  // Carica i form quando selectedProject cambia
  useEffect(() => {
    const fetchForms = async () => {
      if (!selectedProject?._id) return;
      setLoadingForms(true);
      try {
        const fetchedForms = await getFormsByProjectId(selectedProject._id);
        setForms(fetchedForms);
      } catch (error: any) {
        if (error.response?.status === 404) setForms([]);
        else {
          console.error("Error fetching forms:", error);
          showToast("Couldn't load forms for this project.", "error");
        }
      } finally {
        setLoadingForms(false);
      }
    };
    fetchForms();
  }, [selectedProject, showToast]);

  // Gestione modifica / creazione tramite URL
  useEffect(() => {
    if (formId && forms.length > 0) {
      const formToEdit = forms.find((f) => f._id === formId);
      if (formToEdit) {
        setEditingForm(formToEdit);
        setSelectedForm(formToEdit);
        setCreatingNew(true);
      }
    }
  }, [formId, forms]);

  useEffect(() => {
    document.title = "Forms - FormsAware";
  }, []);

  const toggleAccordion = (formId: string) => {
    setOpenFormIds((prev) =>
      prev.includes(formId)
        ? prev.filter((id) => id !== formId)
        : [...prev, formId],
    );
  };

  const fetchSubmissions = async (formId: string) => {
    if (!selectedProject?._id) return;
    try {
      const fetchedSubmissions = await getSubmissionsByFormId(formId);
      setSubmissions(fetchedSubmissions);
      setSubmissionsPage(true);
    } catch (error: any) {
      console.error("Error fetching submissions:", error);
      setSubmissions(undefined);
      setSubmissionsPage(true);
      showToast("Couldn't load submissions for this form.", "error");
    }
  };

  const submissionGoBack = () => {
    setSubmissionsPage(false);
    setSelectedForm(undefined);
    setSubmissions(undefined);
  };

  const handleUpdateForm = (updatedForm: FormFA) => {
    const wasEditing = forms.some((f) => f._id === updatedForm._id);
    setForms((prev) =>
      wasEditing
        ? prev.map((f) => (f._id === updatedForm._id ? updatedForm : f))
        : [...prev, updatedForm],
    );
    setCreatingNew(false);
    setEditingForm(null);
    setSelectedForm(undefined);
    showToast(wasEditing ? "Form updated." : "Form created.", "success");
  };

  const handleConfirmDelete = async () => {
    if (!selectedForm || !selectedProject) return;
    try {
      await deleteForm(selectedProject._id!, selectedForm._id!);
      setForms(forms.filter((f) => f._id !== selectedForm._id));
      showToast(`"${selectedForm.name}" was deleted.`, "success");
    } catch (err) {
      console.error("Error deleting form:", err);
      showToast("Couldn't delete this form. Please try again.", "error");
    } finally {
      setIsConfirmOpen(false);
    }
  };

  const filteredForms = forms.filter((f) =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <>
      <ConfirmModal
        isOpen={isConfirmOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={() => {
          setIsConfirmOpen(false);
          setModalShowDeleteBtn(false);
          setSelectedForm(undefined);
        }}
        onConfirm={handleConfirmDelete}
        showDeleteButton={modalShowDeleteBtn}
      />

      <div className="w-full h-full flex flex-col">
        {creatingNew ? (
          <div className="w-full h-full flex flex-col">
            <div className="flex gap-3 items-center mb-6 shrink-0">
              <button
                onClick={() => {
                  setCreatingNew(false);
                  setEditingForm(null);
                  setSelectedForm(undefined);
                  navigate(`/forms`);
                }}
                className="size-9 flex items-center justify-center rounded-full text-ink-muted hover:text-primary hover:bg-surface-hover transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-2xl font-display font-bold text-ink">
                {editingForm
                  ? "Edit " + (selectedForm?.name ?? "")
                  : "Create New Form"}
              </h1>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              <UpsertProjectForm
                onSubmit={handleUpdateForm}
                initialData={{
                  form_id: selectedForm?._id,
                  project_id: selectedProject?._id!,
                }}
              />
            </div>
          </div>
        ) : submissionsPage && selectedForm ? (
          <Submissions
            formName={selectedForm.name}
            submissions={submissions}
            onBack={submissionGoBack}
          />
        ) : (
          <>
            <div className="flex flex-row items-center justify-between w-full mb-6 shrink-0">
              <div>
                <h1 className="text-2xl font-display font-bold text-ink">
                  {selectedProject?.name ?? "Selected Project"}
                </h1>
                <p className="text-sm text-ink-muted mt-1">
                  Forms belonging to this project.
                </p>
              </div>
              <Button
                icon={Plus}
                onClick={() => {
                  setEditingForm(null);
                  setSelectedForm(undefined);
                  setCreatingNew(true);
                }}
              >
                New Form
              </Button>
            </div>

            <div className="mb-5 shrink-0">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search forms..."
              />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
              {loadingForms ? (
                <Loader />
              ) : filteredForms.length > 0 ? (
                filteredForms.map((form, i) => {
                  const isOpen = openFormIds.includes(form._id!);
                  return (
                    <Card
                      key={form._id}
                      padding="none"
                      className="animate-fade-in-up"
                      style={staggerDelay(i)}
                    >
                      <button
                        onClick={() => toggleAccordion(form._id!)}
                        className="w-full text-left p-4 flex justify-between items-center cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-9 shrink-0 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
                            <FileText size={16} />
                          </div>
                          <span className="text-sm font-semibold text-ink truncate">
                            {form.name}
                          </span>
                          {form.enabled ? (
                            <Badge tone="success">Enabled</Badge>
                          ) : (
                            <Badge tone="error">Disabled</Badge>
                          )}
                        </div>

                        <div className="flex flex-row items-center gap-4 shrink-0">
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingForm(form);
                              setSelectedForm(form);
                              setCreatingNew(true);
                              navigate(`/forms/${form._id}`);
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                          >
                            <Pencil size={12} />
                            Edit
                          </span>
                          <ChevronDown
                            size={16}
                            className={`text-ink-faint transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>

                      {isOpen && (
                        <div className="px-4 pb-4 border-t border-border pt-4 animate-fade-in-down animate-duration-200">
                          <div className="flex flex-row items-center gap-2">
                            <Button
                              variant="danger"
                              size="sm"
                              icon={Trash2}
                              onClick={() => {
                                setSelectedForm(form);
                                setIsConfirmOpen(true);
                                setModalShowDeleteBtn(true);
                                setModalTitle("Confirm Deletion");
                                setModalMessage(
                                  `Are you sure you want to delete ${form.name}?`,
                                );
                              }}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={Inbox}
                              onClick={() => {
                                setSelectedForm(form);
                                fetchSubmissions(form._id!);
                              }}
                            >
                              Submissions
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })
              ) : (
                <EmptyState
                  icon={Search}
                  title="No forms found in this project"
                  description="Try a different search term, or create a new form."
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};
